import { useEffect, useRef } from 'react';

const EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'] as const;

/** Calls onWarn `warnSeconds` before `minutes` of inactivity elapse, then onTimeout. Any activity re-arms both timers. */
export function useIdleTimeout(opts: { minutes: number; onWarn: () => void; onTimeout: () => void; warnSeconds?: number }) {
  const { minutes, onWarn, onTimeout, warnSeconds = 60 } = opts;
  const cbs = useRef({ onWarn, onTimeout });
  useEffect(() => {
    cbs.current = { onWarn, onTimeout };
  }, [onWarn, onTimeout]);

  useEffect(() => {
    if (minutes <= 0) return;
    const totalMs = minutes * 60_000;
    let warnTimer: number | null = null;
    let outTimer: number | null = null;
    const clear = () => {
      if (warnTimer) window.clearTimeout(warnTimer);
      if (outTimer) window.clearTimeout(outTimer);
    };
    const arm = () => {
      clear();
      warnTimer = window.setTimeout(() => cbs.current.onWarn(), Math.max(0, totalMs - warnSeconds * 1000));
      outTimer = window.setTimeout(() => cbs.current.onTimeout(), totalMs);
    };
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < 1000) return;
      last = now;
      arm();
    };
    arm();
    EVENTS.forEach((e) => document.addEventListener(e, onActivity, { passive: true }));
    return () => {
      EVENTS.forEach((e) => document.removeEventListener(e, onActivity));
      clear();
    };
  }, [minutes, warnSeconds]);
}
